// Facade over window.Telegram.WebApp. Outside the WebView (local browser
// dev) the global is undefined; every accessor returns a safe fallback so
// the page still renders.

interface TelegramUser {
  readonly id?: number;
  readonly first_name?: string;
  readonly last_name?: string;
  readonly username?: string;
}

interface TelegramHapticFeedback {
  impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
  notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
}

// BiometricManager, Bot API 7.2+. The token it stores goes into the device's
// Keychain (iOS) or Keystore (Android), encrypted at rest and unreadable by
// other apps — which is what makes it usable as a second factor rather than
// as a convenience.
interface TelegramBiometricManager {
  readonly isInited: boolean;
  readonly isBiometricAvailable: boolean;
  readonly biometricType: 'finger' | 'face' | 'unknown';
  readonly isAccessGranted: boolean;
  readonly isBiometricTokenSaved: boolean;
  readonly deviceId: string;
  init: (cb?: () => void) => void;
  requestAccess: (params: { reason?: string }, cb?: (granted: boolean) => void) => void;
  authenticate: (
    params: { reason?: string },
    cb?: (ok: boolean, token?: string) => void,
  ) => void;
  updateBiometricToken: (token: string, cb?: (updated: boolean) => void) => void;
  openSettings?: () => void;
}

interface TelegramWebApp {
  readonly initData: string;
  readonly BiometricManager?: TelegramBiometricManager;
  readonly initDataUnsafe?: { readonly user?: TelegramUser };
  readonly HapticFeedback?: TelegramHapticFeedback;
  ready: () => void;
  expand: () => void;
  close?: () => void;
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
  SettingsButton?: {
    show?: () => void;
    hide?: () => void;
    onClick?: (cb: () => void) => void;
    offClick?: (cb: () => void) => void;
  };
  showAlert?: (msg: string) => void;
  showConfirm?: (msg: string, cb: (confirmed: boolean) => void) => void;
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string) => void;
}

interface WindowWithTelegram {
  Telegram?: { WebApp?: TelegramWebApp };
}

export const getWebApp = (): TelegramWebApp | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as WindowWithTelegram).Telegram?.WebApp;
};

export const getInitData = (): string => getWebApp()?.initData ?? '';

export const getTelegramUser = (): TelegramUser | undefined =>
  getWebApp()?.initDataUnsafe?.user;

// Forces the WebView chrome to match the app canvas regardless of the
// user's Telegram color scheme. The color comes from the active theme's
// brand module so the chrome follows the built theme. Wrapped in
// try/catch because older Telegram clients throw on setHeaderColor.
export const readyAndExpand = (chromeColor: string): void => {
  const wa = getWebApp();
  if (!wa) return;
  try { wa.ready(); } catch { /* noop */ }
  try { wa.expand(); } catch { /* noop */ }
  try { wa.setHeaderColor?.(chromeColor); } catch { /* noop */ }
  try { wa.setBackgroundColor?.(chromeColor); } catch { /* noop */ }
};

export const closeWebApp = (): void => {
  try { getWebApp()?.close?.(); } catch { /* noop */ }
};

// Adds Telegram's "Settings" item to the WebView's ⋯ menu and routes a tap
// through `onClick`. Returns a cleanup that removes the handler and hides the
// item, so a React effect can register on mount and tear down on unmount
// without leaking duplicate handlers. No-ops (still returns a cleanup) on
// clients older than Bot API 6.10 that don't expose SettingsButton.
export const wireSettingsButton = (onClick: () => void): (() => void) => {
  const button = getWebApp()?.SettingsButton;
  if (!button) return (): void => { /* nothing wired */ };
  try { button.onClick?.(onClick); } catch { /* noop */ }
  try { button.show?.(); } catch { /* noop */ }
  return (): void => {
    try { button.offClick?.(onClick); } catch { /* noop */ }
    try { button.hide?.(); } catch { /* noop */ }
  };
};

// Opens a t.me deep-link inside the Telegram client. WebApp v6.1+
// exposes openTelegramLink for this exact purpose — Telegram intercepts
// the URL and routes the user to the relevant chat / bot without
// leaving the app. The fallback to plain window.location keeps dev
// browsers (no WebApp) working: the user gets a standard navigation
// to t.me/... which Telegram's desktop client handles via its own
// system-protocol handler.
export const openTelegramLink = (url: string): void => {
  const wa = getWebApp();
  if (wa?.openTelegramLink) {
    try { wa.openTelegramLink(url); return; } catch { /* fall through */ }
  }
  if (typeof window !== 'undefined') {
    window.location.assign(url);
  }
};

// Opens an arbitrary https URL in the user's external system browser via
// WebApp.openLink — the Telegram client launches the OS browser and
// keeps the Mini App alive in the background. Used for the kyc-host SPA
// so the Sumsub iframe runs on a permitted origin (mobile WebViews block
// camera access from inside the Telegram in-app browser). Falls back to
// window.open for dev browsers without the WebApp global.
export const openExternalLink = (url: string): void => {
  const wa = getWebApp();
  if (wa?.openLink) {
    try { wa.openLink(url); return; } catch { /* fall through */ }
  }
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const showAlert = (msg: string): void => {
  const wa = getWebApp();
  if (wa?.showAlert) {
    try { wa.showAlert(msg); return; } catch { /* fall through */ }
  }
  if (typeof window !== 'undefined') window.alert(msg);
};

export const showConfirm = (msg: string, cb: (confirmed: boolean) => void): void => {
  const wa = getWebApp();
  if (wa?.showConfirm) {
    try { wa.showConfirm(msg, cb); return; } catch { /* fall through */ }
  }
  if (typeof window !== 'undefined') cb(window.confirm(msg));
  else cb(false);
};

// Haptic helpers feature-detect and silently no-op on platforms that
// don't expose the API (Telegram desktop, dev browsers). Both helpers
// also respect prefers-reduced-motion since vestibular triggers
// commonly accompany motion sensitivity.
const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const hapticImpact = (style: 'light' | 'medium' | 'heavy' = 'light'): void => {
  if (prefersReducedMotion()) return;
  try {
    getWebApp()?.HapticFeedback?.impactOccurred?.(style);
  } catch {
    /* noop */
  }
};

export const hapticNotify = (type: 'success' | 'error' | 'warning'): void => {
  if (prefersReducedMotion()) return;
  try {
    getWebApp()?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    /* noop */
  }
};

// ── Biometrics ────────────────────────────────────────────────────────────
//
// Every call is wrapped twice: in try/catch, because older clients throw on
// the property access itself, and in a timeout, because init and the callback
// APIs are known to never fire on some clients. A second factor that can hang
// forever is worse than one that reports itself unavailable.

const BIOMETRIC_TIMEOUT_MS = 5000;

export interface BiometricState {
  readonly available: boolean;
  readonly accessGranted: boolean;
  readonly tokenSaved: boolean;
  readonly deviceId: string;
  readonly type: 'finger' | 'face' | 'unknown';
}

const UNAVAILABLE: BiometricState = {
  available: false,
  accessGranted: false,
  tokenSaved: false,
  deviceId: '',
  type: 'unknown',
};

const getBiometric = (): TelegramBiometricManager | undefined => {
  try {
    return getWebApp()?.BiometricManager;
  } catch {
    return undefined;
  }
};

const withTimeout = <T>(run: (resolve: (value: T) => void) => void, fallback: T): Promise<T> =>
  new Promise<T>((resolve) => {
    let settled = false;
    const finish = (value: T): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = setTimeout(() => { finish(fallback); }, BIOMETRIC_TIMEOUT_MS);
    try {
      run((value) => {
        clearTimeout(timer);
        finish(value);
      });
    } catch {
      clearTimeout(timer);
      finish(fallback);
    }
  });

const readState = (manager: TelegramBiometricManager): BiometricState => ({
  available: manager.isBiometricAvailable,
  accessGranted: manager.isAccessGranted,
  tokenSaved: manager.isBiometricTokenSaved,
  deviceId: manager.deviceId,
  type: manager.biometricType,
});

export const initBiometrics = async (): Promise<BiometricState> => {
  const manager = getBiometric();
  if (manager === undefined) return UNAVAILABLE;
  if (manager.isInited) return readState(manager);
  return withTimeout<BiometricState>((resolve) => {
    manager.init(() => { resolve(readState(manager)); });
  }, UNAVAILABLE);
};

// Asked once per account, the first time a device is enrolled. `reason` is
// shown by the OS prompt, so it says what the app will do with the answer.
export const requestBiometricAccess = (reason: string): Promise<boolean> => {
  const manager = getBiometric();
  if (manager === undefined) return Promise.resolve(false);
  if (manager.isAccessGranted) return Promise.resolve(true);
  return withTimeout<boolean>((resolve) => {
    manager.requestAccess({ reason }, resolve);
  }, false);
};

export interface BiometricAuthResult {
  readonly ok: boolean;
  readonly token: string;
}

export const authenticateBiometrics = (reason: string): Promise<BiometricAuthResult> => {
  const manager = getBiometric();
  if (manager === undefined) return Promise.resolve({ ok: false, token: '' });
  return withTimeout<BiometricAuthResult>((resolve) => {
    manager.authenticate({ reason }, (ok, token) => {
      resolve({ ok, token: token ?? '' });
    });
  }, { ok: false, token: '' });
};

export const saveBiometricToken = (token: string): Promise<boolean> => {
  const manager = getBiometric();
  if (manager === undefined) return Promise.resolve(false);
  return withTimeout<boolean>((resolve) => {
    manager.updateBiometricToken(token, resolve);
  }, false);
};
